import { parseDay } from '@map-my-money/engine';
import { useEffect, useId, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError, api, upgradeOf } from '../api/client.ts';
import type { ShareDraftDetail, UpgradeRequired } from '../api/types.ts';
import { preparedBy } from '../components/DraftBanner.tsx';
import { Icon } from '../components/Icon.tsx';
import { PageHeader, firstName } from '../components/Shell.tsx';
import { UpgradeNotice } from '../components/UpgradePrompt.tsx';
import { t } from '../i18n/index.ts';
import { now } from '../lib/clock.ts';
import { fullDate } from '../lib/format.ts';
import { useIsDesktop } from '../lib/hooks.ts';
import { useStore } from '../state/store.tsx';
import { useToast } from '../state/toast.tsx';
import { LinkReady } from './ShareCreate.tsx';
import { SharedMapView, type SharedMapSource } from './SharedMap.tsx';
import { useDocumentTitle } from '../lib/documentTitle.ts';

type State =
  | { status: 'loading' }
  | { status: 'ready'; detail: ShareDraftDetail }
  | { status: 'missing' }
  | { status: 'error' };

const isMapDraft = (d: ShareDraftDetail): d is Extract<ShareDraftDetail, { draft: { kind: 'map' } }> => d.draft.kind === 'map';

const localDay = (iso: string) => {
  const at = new Date(iso);
  return fullDate(parseDay(`${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, '0')}-${String(at.getDate()).padStart(2, '0')}`));
};

export function ShareConfirmScreen() {
  useDocumentTitle(t('docTitle.confirmShare'));
  const { id = '' } = useParams();
  const desktop = useIsDesktop();
  const [state, setState] = useState<State>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    setState({ status: 'loading' });
    api.shareDraft(id).then(
      (detail) => live && setState({ status: 'ready', detail }),
      (e) => live && setState({ status: e instanceof ApiError && e.status === 404 ? 'missing' : 'error' }),
    );
    return () => {
      live = false;
    };
  }, [id, attempt]);

  const className = desktop ? 'page share-new share-confirm' : 'page page--mobile share-new share-confirm';
  if (state.status === 'ready') return <Confirm detail={state.detail} className={className} desktop={desktop} />;
  if (state.status === 'loading') {
    return (
      <div className={className} aria-busy="true">
        <p className="sr-only" role="status">{t('common.loading')}</p>
        <div className="skeleton skeleton--header" />
        <div className="skeleton skeleton--hero" />
      </div>
    );
  }
  return (
    <div className="state-panel" role={state.status === 'error' ? 'alert' : undefined}>
      <span className="state-panel__icon state-panel__icon--amber" aria-hidden="true"><Icon name={state.status === 'error' ? 'alert' : 'link'} size={26} /></span>
      <h1 className="state-panel__title">{state.status === 'error' ? t('draft.error.title') : t('draft.missing.title')}</h1>
      <p className="state-panel__body">{state.status === 'error' ? t('error.body') : t('draft.missing.body')}</p>
      {state.status === 'error' ? (
        <button type="button" className="button button--primary" onClick={() => setAttempt((a) => a + 1)}>{t('common.retry')}</button>
      ) : (
        <Link to="/share" className="button button--primary">{t('draft.missing.cta')}</Link>
      )}
    </div>
  );
}

function Confirm({ detail, className, desktop }: { detail: ShareDraftDetail; className: string; desktop: boolean }) {
  const { draft } = detail;
  const { me, refreshDrafts, refreshShares } = useStore();
  const navigate = useNavigate();
  const toast = useToast();
  const id = useId();
  const [busy, setBusy] = useState<'confirm' | 'discard' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [upgrade, setUpgrade] = useState<UpgradeRequired | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const by = preparedBy(draft.createdVia);
  const kindName = t(`shareNew.kind.${draft.kind}`);

  const source = useMemo<SharedMapSource>(() => (isMapDraft(detail)
    ? { kind: 'map', asOf: detail.data.asOf, plan: detail.data.plan, anchor: detail.data.anchor ?? null }
    : { kind: 'template', asOf: detail.data.asOf, template: detail.data }), [detail]);
  const meta = useMemo(() => ({
    title: draft.title,
    ownerFirstName: firstName(me?.user.displayName, me?.user.email) || null,
    expiresAt: new Date(now().getTime() + draft.expiresInDays * 86_400_000).toISOString(),
  }), [draft, me]);

  const confirm = async () => {
    setBusy('confirm');
    setError(null);
    setUpgrade(null);
    try {
      const result = await api.confirmShareDraft(draft.id);
      setUrl(result.url);
      void refreshDrafts();
      void refreshShares();
    } catch (e) {
      const needed = upgradeOf(e);
      if (needed) {
        setUpgrade(needed);
      } else if (e instanceof ApiError && e.status === 404) {
        void refreshDrafts();
        setError(t('draft.missing.body'));
      } else {
        setError(e instanceof ApiError && (e.isPlanTooLarge || e.status === 422) ? e.message : t('draft.error.confirm'));
      }
    } finally {
      setBusy(null);
    }
  };

  const discard = async () => {
    setBusy('discard');
    setError(null);
    try {
      await api.discardShareDraft(draft.id);
      await refreshDrafts();
      toast.push({ tone: 'success', message: t('draft.discarded') });
      navigate('/share');
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        await refreshDrafts();
        navigate('/share');
        return;
      }
      setError(t('draft.error.discard'));
      setBusy(null);
    }
  };

  const points = draft.kind === 'map' ? [t('draft.map.1'), t('draft.map.2')] : [t('draft.template.1'), t('draft.template.2')];
  const title = t('draft.title', { title: draft.title });
  const subtitle = t('draft.subtitle', { name: by });
  return (
    <div className={className}>
      {desktop ? (
        <PageHeader eyebrow={t('draft.eyebrow')} title={title} subtitle={subtitle} />
      ) : (
        <header className="stack-xs">
          <Link to="/share" className="text-link text-link--icon share-confirm__back"><Icon name="chevronLeft" size={14} />{t('shareNew.eyebrow')}</Link>
          <span className="eyebrow">{t('draft.eyebrow')}</span>
          <h1 className="mobile-title share-confirm__title">{title}</h1>
          <p className="page-subtitle">{subtitle}</p>
        </header>
      )}
      <div className="share-new__grid">
        <div className="stack-lg">
          <section className="card card--pad-lg stack-md" aria-labelledby={`${id}-details`}>
            <h2 className="eyebrow" id={`${id}-details`}>{t('draft.details')}</h2>
            <dl className="draft-facts">
              <div><dt>{t('draft.preparedBy')}</dt><dd className="draft-facts__by"><span className="source-badge source-badge--claude" title={by}>{by}</span></dd></div>
              <div><dt>{t('draft.plan')}</dt><dd>{draft.planName}</dd></div>
              <div><dt>{t('draft.kind')}</dt><dd><span className={`share-badge share-badge--${draft.kind}`}>{kindName}</span></dd></div>
              <div><dt>{t('draft.linkExpires')}</dt><dd>{t('draft.linkExpiresValue', { n: draft.expiresInDays })}</dd></div>
              <div><dt>{t('draft.draftExpires')}</dt><dd>{localDay(draft.expiresAt)}</dd></div>
            </dl>
          </section>
          {url ? (
            <section className="card card--pad-lg stack-md">
              <LinkReady url={url} />
              <p className="fine-print">{t('draft.doneBody')}</p>
            </section>
          ) : (
            <section className="card card--pad-lg stack-md" aria-labelledby={`${id}-public`}>
              <div className="share-new__warning" role="note">
                <Icon name="alert" size={17} />
                <div className="stack-xs">
                  <h2 className="share-confirm__public" id={`${id}-public`}>{t('draft.public')}</h2>
                  <ul className="share-confirm__points">
                    {points.map((p) => <li key={p}>{p}</li>)}
                    <li>{t('draft.frozen')}</li>
                  </ul>
                </div>
              </div>
              {error && <p className="share-new__error" role="alert"><Icon name="alert" size={15} />{error}</p>}
              {upgrade && <UpgradeNotice upgrade={upgrade} />}
              <div className="button-row share-confirm__actions">
                <button type="button" className="button button--primary" disabled={busy !== null} onClick={() => void confirm()}>
                  <Icon name="check" size={16} strokeWidth={2.4} />{busy === 'confirm' ? t('draft.confirming') : t('draft.confirm')}
                </button>
                <button type="button" className="button button--ghost" disabled={busy !== null} onClick={() => void discard()}>
                  {t('draft.discard')}
                </button>
              </div>
            </section>
          )}
        </div>
        <section className="share-new__preview" aria-labelledby={`${id}-preview`}>
          <div className="section-header">
            <h2 className="eyebrow" id={`${id}-preview`}>{t('draft.preview')}</h2>
            <span className="share-new__hint">{t('draft.previewHint')}</span>
          </div>
          <div className="share-new__frame" inert>
            <SharedMapView source={source} meta={meta} preview />
          </div>
        </section>
      </div>
    </div>
  );
}
